import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import shortUUID from "short-uuid";

interface UseAutoOpenModalConfig {
  parameter?: string;
  redirectPath?: string;
  getDefaultFormData?: () => any;
  trigger?: any;
}

interface UseAutoOpenModalReturn {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  defaultFormData: any;
  setDefaultFormData: (data: any) => void;
}

/**
 * Custom hook for automatically opening modals based on URL parameters
 * @param config - Configuration object
 * @param config.parameter - The URL parameter to watch for (e.g., 'new')
 * @param config.redirectPath - The path to redirect to after opening modal
 * @param config.getDefaultFormData - Function that returns default form data
 * @param config.trigger - Dependency that triggers the effect (e.g., caseInfo)
 * @returns { showModal, setShowModal, defaultFormData, setDefaultFormData }
 */
export const useAutoOpenModal = ({
  parameter = "new",
  redirectPath,
  getDefaultFormData = () => ({}),
  trigger,
}: UseAutoOpenModalConfig): UseAutoOpenModalReturn => {
  const [showModal, setShowModal] = useState(false);
  const [defaultFormData, setDefaultFormData] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.search.includes(parameter) && trigger) {
      const formData = getDefaultFormData();
      setDefaultFormData(formData);
      setShowModal(true);

      // Clean up the URL parameter after opening modal
      if (redirectPath) {
        navigate(redirectPath, { replace: true });
      }
    }
  }, [
    location.search,
    parameter,
    redirectPath,
    navigate,
    trigger,
    getDefaultFormData,
  ]);

  return {
    showModal,
    setShowModal,
    defaultFormData,
    setDefaultFormData,
  };
};

export default useAutoOpenModal;
